import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { fromISODate } from '../components/filters/dateRange';
import { MAX_FILTER_SELECTION } from '../components/filters/EntityFilter';

export interface UrlParam<T> {
  default: T;
  // Invalid raw values return undefined, so the hook uses the default.
  parse: (raw: string) => T | undefined;
  serialize?: (value: T) => string;
}

export type UrlSpec = Record<string, UrlParam<any>>;

export type UrlState<S extends UrlSpec> = {
  [K in keyof S]: S[K] extends UrlParam<infer T> ? T : never;
};

function serialize<T>(param: UrlParam<T>, value: T): string {
  return param.serialize?.(value) ?? String(value);
}

function paramKey(key: string, prefix?: string): string {
  return prefix ? `${prefix}.${key}` : key;
}

// The last write the router has not rendered yet. Two writers in one commit,
// such as two page clamps on one route, would otherwise both start from the
// same stale params and the last one would win. Cleared once the router
// renders the written search.
let pending: { pathname: string; fromSearch: string; toSearch: string } | null = null;

// Define spec objects outside components so their identity stays stable.
export function useUrlState<S extends UrlSpec>(
  spec: S,
  prefix?: string,
): [UrlState<S>, (patch: Partial<UrlState<S>>) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname, state: routerState } = useLocation();
  const search = searchParams.toString();

  useEffect(() => {
    if (pending && pending.pathname === pathname && pending.toSearch === search) pending = null;
  }, [pathname, search]);

  const state = useMemo(() => {
    const current = new URLSearchParams(search);
    const next = {} as UrlState<S>;
    (Object.keys(spec) as Array<keyof S>).forEach((key) => {
      const param = spec[key];
      const raw = current.get(paramKey(String(key), prefix));
      next[key] = (raw === null ? param.default : param.parse(raw) ?? param.default) as UrlState<S>[typeof key];
    });
    return next;
  }, [prefix, search, spec]);

  const set = useCallback(
    (patch: Partial<UrlState<S>>) => {
      const base = pending && pending.pathname === pathname && pending.fromSearch === search
        ? pending.toSearch
        : search;
      const next = new URLSearchParams(base);
      (Object.keys(patch) as Array<keyof S>).forEach((key) => {
        const param = spec[key];
        const value = patch[key] as UrlState<S>[typeof key];
        const name = paramKey(String(key), prefix);
        const serialized = serialize(param, value);
        if (serialized === serialize(param, param.default)) {
          next.delete(name);
        } else {
          next.set(name, serialized);
        }
      });
      // Still one patch per event: writes are navigations, not batched state.
      pending = { pathname, fromSearch: search, toSearch: next.toString() };
      // Keep the router state, such as the Back target of a detail page.
      setSearchParams(next, { replace: true, state: routerState });
    },
    [pathname, prefix, routerState, search, setSearchParams, spec],
  );

  return [state, set];
}

export function urlString(def = ''): UrlParam<string> {
  return { default: def, parse: (raw) => raw };
}

export function urlEnum<T extends string>(values: readonly T[], def: T): UrlParam<T> {
  return {
    default: def,
    parse: (raw) => values.find((value) => value === raw),
  };
}

export function urlPositiveInt(def = 1): UrlParam<number> {
  return {
    default: def,
    parse: (raw) => {
      if (!/^\d+$/.test(raw)) return undefined;
      const value = Number(raw);
      return value >= 1 && Number.isSafeInteger(value) ? value : undefined;
    },
  };
}

export function urlBool(def = false): UrlParam<boolean> {
  return {
    default: def,
    parse: (raw) => {
      if (raw === '1') return true;
      if (raw === '0') return false;
      return undefined;
    },
    serialize: (value) => (value ? '1' : '0'),
  };
}

export function urlIsoDate(): UrlParam<string> {
  return {
    default: '',
    parse: (raw) => (fromISODate(raw) ? raw : undefined),
  };
}

export function urlIdList(): UrlParam<number[]> {
  return {
    default: [],
    parse: (raw) => {
      const values = raw.split(',');
      if (values.some((value) => !/^\d+$/.test(value))) return undefined;
      const ids = values.map(Number);
      if (ids.some((id) => id < 1 || !Number.isSafeInteger(id))) return undefined;
      return Array.from(new Set(ids)).sort((a, b) => a - b).slice(0, MAX_FILTER_SELECTION);
    },
    serialize: (value) => value.join(','),
  };
}

export function urlOptionalId(): UrlParam<number | undefined> {
  return {
    default: undefined,
    parse: (raw) => urlPositiveInt().parse(raw),
    serialize: (value) => (value === undefined ? '' : String(value)),
  };
}
